import React from "react";
import DatePickerInput from "./DatePickerInput";

/**
 * Shared popup positioning for `react-datepicker` on admin screens.
 *
 * By default the calendar is rendered inline, next to its input, and positioned
 * against the nearest positioned ancestor. Inside the admin shell — whose
 * content region scrolls — that measurement is off, and the tall
 * `showTimeSelect` variant flips above the field and is clipped off the top of
 * the page instead of opening under it.
 *
 * Rendering into a body-level portal with viewport ("fixed") coordinates takes
 * the surrounding layout out of the calculation: the popup can no longer be
 * clipped by a scrolling or overflow-hidden ancestor, and floating-ui keeps it
 * pinned to the input as the page scrolls.
 *
 * Spread onto any admin `<DatePicker>`:
 *
 *   <DatePicker {...datePickerPopperProps} selected={value} ... />
 *
 * The portal node is created on first use — no markup needed in index.html.
 *
 * `.react-datepicker`'s base stylesheet rule in index.css deliberately keeps
 * every calendar transparent (for calendars embedded inline in their own
 * styled containers). Every popup that opens over other content — which is
 * every use of this shared portal — needs its own opaque surface, so
 * `st-admin-datepicker-popup` below gives it one.
 */
export const datePickerPopperProps = {
  portalId: "admin-datepicker-portal",
  popperPlacement: "bottom-start" as const,
  popperProps: { strategy: "fixed" as const },
  // Above the fixed sidebar (z-40). Forced, because react-datepicker's own
  // stylesheet pins `.react-datepicker-popper` to `z-index: 1` and which of the
  // two lands last in the bundle isn't something to rely on.
  popperClassName: "!z-50 st-admin-datepicker-popup",
  // Suppresses the on-screen keyboard that would otherwise cover the calendar
  // on a phone. See DatePickerInput for why react-datepicker's own `readOnly`
  // prop can't be used for this. createElement rather than JSX so this stays a
  // .ts file and every existing import path keeps working.
  customInput: React.createElement(DatePickerInput),
};

export default datePickerPopperProps;
